// Duplicate detection for spoke names.
//
// The Backstage database is PostgreSQL in production but in-memory SQLite for
// local dev and the E2E suite, so DB-native matching is not portable. The
// comparison therefore runs in the application layer against the list of
// existing spoke names, which keeps it simple, testable and database-agnostic.
//
// Only EXACT duplicates (after normalisation) are blocked. A near-duplicate
// (edit-distance) check used to live here too; it was removed deliberately
// because it over-blocked legitimate distinct names.

export const normaliseSpokeName = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export interface SpokeNameConflict {
  existingName: string;
}

export const findSpokeNameConflict = (
  candidate: string,
  existingNames: string[],
): SpokeNameConflict | undefined => {
  const normalisedCandidate = normaliseSpokeName(candidate);
  if (!normalisedCandidate) return undefined;

  for (const name of existingNames) {
    if (normaliseSpokeName(name) === normalisedCandidate) {
      return { existingName: name };
    }
  }

  return undefined;
};

export const spokeNameConflictMessage = (conflict: SpokeNameConflict): string =>
  `A spoke named "${conflict.existingName}" already exists. Please choose a different name.`;

// Shared store of existing spoke names. The field component populates this once
// it has fetched the current spokes; the synchronous scaffolder validation
// callback reads it (validation cannot itself perform an async fetch).
let knownSpokeNames: string[] = [];

export const setKnownSpokeNames = (names: string[]): void => {
  knownSpokeNames = names;
};

export const getKnownSpokeNames = (): string[] => knownSpokeNames;
